import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';

const SPOT_INTERRUPTION_PREDICTION_PERFORMANCE_SUMMARY_QUERY_KEY =
  'spot-interruption-prediction-performance-summary';

type UseSpotInterruptionPredictionPerformanceSummaryQueryParams = {
  organizationId?: string;
  clusterIds: string[];
  key: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  nodeTemplateNames?: string[];
  enabled?: boolean;
};

const getSpotInterruptionPredictionPerformanceSummaryQueryKey = ({
  organizationId,
  clusterIds,
  key,
  nodeTemplateNames,
}: Pick<
  UseSpotInterruptionPredictionPerformanceSummaryQueryParams,
  'organizationId' | 'clusterIds' | 'key' | 'nodeTemplateNames'
>) =>
  [
    SPOT_INTERRUPTION_PREDICTION_PERFORMANCE_SUMMARY_QUERY_KEY,
    organizationId,
    clusterIds,
    key,
    nodeTemplateNames,
  ] as const;

const getSpotInterruptionPredictionPerformanceSummaryQueryOptions = ({
  organizationId,
  clusterIds,
  key,
  startTime,
  endTime,
  timeZone,
  nodeTemplateNames,
  enabled = true,
}: UseSpotInterruptionPredictionPerformanceSummaryQueryParams) =>
  queryOptions({
    queryKey: getSpotInterruptionPredictionPerformanceSummaryQueryKey({
      organizationId,
      clusterIds,
      key,
      nodeTemplateNames,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getSpotInterruptionPredictionPerformanceSummary(
          {
            organizationId: organizationId!,
            clusterIds,
            startTime,
            endTime,
            timeZone,
            nodeTemplateNames,
          }
        );

      return data;
    },
    enabled:
      enabled &&
      !!organizationId &&
      clusterIds.length > 0 &&
      !!startTime &&
      !!endTime,
  });

export const useSpotInterruptionPredictionPerformanceSummaryQuery = (
  props: UseSpotInterruptionPredictionPerformanceSummaryQueryParams
) => {
  return useQuery(
    getSpotInterruptionPredictionPerformanceSummaryQueryOptions(props)
  );
};
